import React, { useEffect, useState, useCallback } from "react";
import { api, setAdminToken } from "@/lib/api";
import { PageShell, SectionLabel, SlideCorner } from "@/components/Layout";
import { toast } from "sonner";
import { ShieldCheck, ChevronDown, Mail, Trash2, Plus, Save, ExternalLink, Youtube, LogOut, ArrowRight, Download, Link as LinkIcon } from "lucide-react";

const TABS = [
  { k: "overview",      label: "Overview" },
  { k: "signups",       label: "RSVPs" },
  { k: "newsletter",    label: "Newsletter" },
  { k: "notify",        label: "Notify Me" },
  { k: "editorial",     label: "Edit" },
  { k: "podcast",       label: "Podcast" },
  { k: "advisors",      label: "Advisors" },
  { k: "settings",      label: "Site Settings" },
  { k: "briefs",        label: "Briefs" },
  { k: "applications",  label: "Applications" },
  { k: "submissions",   label: "Submissions" },
  { k: "bookings",      label: "Bookings" },
  { k: "subscriptions", label: "Subs" },
];

const STATUSES = ["new", "in_review", "accepted", "rejected", "done"];

export default function Admin() {
  const [authState, setAuthState] = useState({ checking: true, admin: false, email: null });
  const [tab, setTab] = useState("overview");
  const [counts, setCounts] = useState({});
  const [data, setData] = useState([]);
  const [progFilter, setProgFilter] = useState("all");
  const [pipeFilter, setPipeFilter] = useState("all");

  // Verify admin status by calling /admin/me (uses admin_session cookie + Bearer fallback).
  // Falls back to /auth/admin-check for legacy Google-OAuth admins.
  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/me");
      setAuthState({ checking: false, admin: !!data.authenticated, email: data.email });
    } catch (_) {
      try {
        const { data } = await api.get("/auth/admin-check");
        setAuthState({ checking: false, admin: !!data.is_admin, email: data.email });
      } catch (__) {
        setAuthState({ checking: false, admin: false, email: null });
      }
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch (_) { /* noop */ }
    setAdminToken(null);
    setAuthState({ checking: false, admin: false, email: null });
    toast.success("Signed out");
  };

  const refreshOverview = useCallback(async () => {
    try { const ov = await api.get("/admin/overview"); setCounts(ov.data); } catch (_) { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    await refreshOverview();
    try {
      const ENDPOINTS = {
        signups:       { url: "/admin/signups" },
        newsletter:    { url: "/admin/newsletter" },
        notify:        { url: "/admin/notify-requests" },
        editorial:     { url: "/admin/editorial" },
        podcast:       { url: "/admin/podcast" },
        advisors:      { url: "/admin/advisors" },
        briefs:        { url: "/admin/briefs" },
        applications:  { url: "/admin/applications", params: progFilter !== "all" ? { program: progFilter } : {} },
        submissions:   { url: "/admin/submissions", params: pipeFilter !== "all" ? { pipeline: pipeFilter } : {} },
        bookings:      { url: "/admin/bookings" },
        subscriptions: { url: "/admin/subscriptions" },
      };
      const cfg = ENDPOINTS[tab];
      if (cfg) {
        const r = await api.get(cfg.url, { params: cfg.params });
        setData(r.data.items || []);
      } else {
        setData([]);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, [tab, progFilter, pipeFilter, refreshOverview]);

  useEffect(() => { if (authState.admin) load(); }, [authState.admin, load]);

  const updateStatus = async (collection, record_id, status) => {
    try { await api.post("/admin/status", { collection, record_id, status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error("Update failed"); }
  };

  if (authState.checking) return <PageShell><div className="p-20 font-mono text-sm">Loading…</div></PageShell>;
  if (!authState.admin) return <AdminLoginScreen onAuthenticated={checkAuth} />;

  const countFor = (k) => {
    const key = k === "subscriptions" ? "active_subs" : k === "signups" ? "signups" : k === "notify" ? "notify_requests" : k;
    return counts[key];
  };

  return (
    <PageShell>
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="ADMIN" />
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-20 pb-10 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <SectionLabel><ShieldCheck size={11} /> Admin · Control room</SectionLabel>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[0.9] tracking-tighter">Everything <span className="hl-coral">on one panel.</span></h1>
            {authState.email && <p className="meta-mono mt-4 !text-white/55">— SIGNED IN AS {authState.email.toUpperCase()}</p>}
          </div>
          <button onClick={logout} data-testid="admin-logout" className="btn-pill"><LogOut size={13} /> Sign out</button>
        </div>
      </section>

      <section className="border-b border-border sticky top-14 z-30 bg-background/95 backdrop-blur">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-3 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)} className={`btn-pill ${tab === t.k ? "btn-coral" : ""}`}>
              {t.label}{countFor(t.k) !== undefined ? ` · ${countFor(t.k)}` : ""}
            </button>
          ))}
        </div>
      </section>

      <section>
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-10">
          {tab === "overview" && <Overview counts={counts} />}

          {tab === "applications" && (
            <div className="mb-4 flex gap-2 flex-wrap">
              {["all","residency","academy","creative_class"].map((p) => (
                <button key={p} onClick={() => setProgFilter(p)} className={`btn-pill ${progFilter === p ? "btn-coral" : ""}`}>{p.replace("_"," ")}</button>
              ))}
            </div>
          )}
          {tab === "submissions" && (
            <div className="mb-4 flex gap-2">
              {["all","publish","release","work"].map((p) => (
                <button key={p} onClick={() => setPipeFilter(p)} className={`btn-pill ${pipeFilter === p ? "btn-coral" : ""}`}>{p}</button>
              ))}
            </div>
          )}

          {tab === "editorial" && <EditorialPanel items={data} reload={load} />}
          {tab === "podcast" && <PodcastPanel items={data} reload={load} />}
          {tab === "advisors" && <AdvisorsPanel items={data} reload={load} />}
          {tab === "settings" && <SettingsPanel />}
          {tab === "newsletter" && <NewsletterPanel items={data} total={counts.newsletter || 0} reload={load} />}
          {tab === "notify" && <NotifyPanel items={data} reload={load} />}
          {tab === "signups" && <SignupsPanel items={data} reload={load} />}

          {["briefs","applications","submissions","bookings","subscriptions"].includes(tab) && (
            <div className="border border-border divide-y divide-border">
              {data.length === 0 ? <p className="p-8 font-mono text-sm text-white/60">No records</p> : data.map((row) => <Row key={row.brief_id || row.application_id || row.submission_id || row.booking_id || row.subscription_id} row={row} tab={tab} onUpdate={updateStatus} onReload={load} />)}
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}

// ============= OVERVIEW =============
function Overview({ counts }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-border">
      {Object.entries(counts).map(([k, v]) => (
        <div key={k} className="bg-background p-6">
          <p className="meta-mono">— {k.replace(/_/g, " ").toUpperCase()}</p>
          <p className="num-display text-4xl md:text-5xl font-bold mt-3 text-primary">{v}</p>
        </div>
      ))}
    </div>
  );
}

// ============= SIGNUPS (RSVPs) =============
function SignupsPanel({ items, reload }) {
  const [selected, setSelected] = useState(new Set());
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const removeOne = async (id) => {
    if (!window.confirm("Delete this RSVP?")) return;
    try { await api.delete(`/admin/signups/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const removeBulk = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} RSVP${selected.size > 1 ? "s" : ""}? This can't be undone.`)) return;
    try {
      const { data } = await api.post("/admin/bulk-delete", { collection: "signups", record_ids: Array.from(selected) });
      toast.success(`${data.deleted} deleted`);
      setSelected(new Set());
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/signups.csv", { responseType: "blob" });
      const stamp = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `anartizen-rsvps-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${items.length} RSVPs`);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };
  if (items.length === 0) return <p className="p-8 font-mono text-sm text-white/60 border border-border">No RSVPs yet.</p>;
  return (
    <div data-testid="admin-rsvps">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="meta-mono !text-white/60">— {items.length} EARLY-ACCESS RSVP{items.length !== 1 ? "S" : ""}</p>
        <button onClick={downloadCsv} className="btn-pill btn-coral" data-testid="rsvp-download-csv">
          <Download size={13} /> Download CSV
        </button>
      </div>
      {selected.size > 0 && (
        <div className="border border-primary/40 bg-primary/[0.05] p-3 mb-3 flex items-center justify-between" data-testid="rsvp-bulk-bar">
          <p className="meta-mono !text-primary">— {selected.size} SELECTED</p>
          <div className="flex gap-2">
            <button onClick={() => setSelected(new Set())} className="btn-pill">Clear</button>
            <button onClick={removeBulk} className="btn-pill btn-coral" data-testid="rsvp-bulk-delete"><Trash2 size={12} /> Delete selected</button>
          </div>
        </div>
      )}
      <div className="border border-border">
        <div className="grid grid-cols-[36px_1fr_1.4fr_1fr_0.9fr_0.9fr_1fr_46px] gap-4 px-4 py-3 border-b border-border meta-mono">
          <span></span><span>— NAME</span><span>EMAIL</span><span>CITY</span><span>DISC.</span><span>ROLE</span><span>WHEN</span><span></span>
        </div>
        {items.map((s) => {
          const id = s.signup_id || s.email;
          return (
            <div key={id} className={`grid grid-cols-[36px_1fr_1.4fr_1fr_0.9fr_0.9fr_1fr_46px] gap-4 px-4 py-3 border-b border-border text-sm hover:bg-white/[0.02] items-center ${selected.has(id) ? "bg-primary/[0.03]" : ""}`}>
              <input type="checkbox" checked={selected.has(id)} onChange={() => toggle(id)} data-testid={`rsvp-check-${id}`} />
              <span className="font-display font-bold truncate">{s.name}</span>
              <span className="text-white/75 truncate">{s.email}</span>
              <span className="text-white/65 truncate">{s.city || "—"}</span>
              <span className="text-white/65 truncate">{s.discipline || "—"}</span>
              <span className="text-white/65 truncate">{s.role || "—"}</span>
              <span className="text-white/45 meta-mono truncate">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</span>
              <button onClick={() => removeOne(id)} className="text-white/40 hover:text-primary" title="Delete RSVP" data-testid={`rsvp-delete-${id}`}><Trash2 size={14} /></button>
              {s.wish && <div className="col-span-8 -mt-1 mb-1 pl-9 italic text-white/55 text-sm">&ldquo;{s.wish}&rdquo;</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============= NEWSLETTER =============
function NewsletterPanel({ items, total, reload }) {
  const removeOne = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try { await api.delete(`/admin/newsletter/${id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const exportCsv = () => {
    const csv = "email,name,source,created_at\n" + items.map((i) => `${i.email},"${(i.name || "").replace(/"/g, '""')}",${i.source || ""},${i.created_at || ""}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `newsletter-${new Date().toISOString().split("T")[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="meta-mono">— {total} SUBSCRIBERS</p>
        <button onClick={exportCsv} data-testid="newsletter-export" className="btn-pill"><ExternalLink size={12} /> Export CSV</button>
      </div>
      <div className="border border-border divide-y divide-border" data-testid="admin-newsletter">
        {items.length === 0 ? <p className="p-8 font-mono text-sm text-white/60">No subscribers yet.</p> : items.map((s) => (
          <div key={s.subscriber_id} className="p-4 grid grid-cols-[1.4fr_1fr_0.8fr_1fr_46px] gap-4 text-sm items-center">
            <span className="font-display font-bold truncate">{s.email}</span>
            <span className="text-white/70 truncate">{s.name || "—"}</span>
            <span className="text-white/60 truncate">{s.source || "—"}</span>
            <span className="text-white/45 meta-mono truncate">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</span>
            <button onClick={() => removeOne(s.subscriber_id)} className="text-white/40 hover:text-primary" title="Remove subscriber" data-testid={`news-delete-${s.subscriber_id}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============= NOTIFY ME (advisor availability leads) =============
function NotifyPanel({ items, reload }) {
  const removeOne = async (id) => {
    if (!window.confirm("Remove this notify request?")) return;
    try { await api.delete(`/admin/notify_requests/${id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const exportCsv = () => {
    const csv =
      "advisor_slug,advisor_name,email,name,created_at\n" +
      items.map((i) =>
        `${i.advisor_slug || ""},"${(i.advisor_name || "").replace(/"/g, '""')}",${i.email},"${(i.name || "").replace(/"/g, '""')}",${i.created_at || ""}`
      ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `notify-requests-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  // Group by advisor so admin sees who's hottest.
  const grouped = items.reduce((acc, r) => {
    const k = r.advisor_slug || "unknown";
    acc[k] = acc[k] || { advisor_name: r.advisor_name || k, rows: [] };
    acc[k].rows.push(r);
    return acc;
  }, {});
  const groups = Object.entries(grouped).sort((a, b) => b[1].rows.length - a[1].rows.length);
  return (
    <div data-testid="admin-notify">
      <div className="flex items-center justify-between mb-4">
        <p className="meta-mono">— {items.length} NOTIFY-ME REQUEST{items.length !== 1 ? "S" : ""} ACROSS {groups.length} ADVISOR{groups.length !== 1 ? "S" : ""}</p>
        <button onClick={exportCsv} data-testid="notify-export" className="btn-pill" disabled={items.length === 0}>
          <Download size={12} /> Export CSV
        </button>
      </div>
      {items.length === 0 ? (
        <p className="p-8 font-mono text-sm text-white/60 border border-border">No notify-me requests yet.</p>
      ) : (
        <div className="space-y-6">
          {groups.map(([slug, g]) => (
            <div key={slug} className="border border-border" data-testid={`notify-group-${slug}`}>
              <div className="px-4 py-3 border-b border-border bg-white/[0.02] flex items-center justify-between">
                <p className="font-display font-bold">{g.advisor_name}</p>
                <p className="meta-mono !text-primary">— {g.rows.length} WAITING</p>
              </div>
              {g.rows.map((r) => (
                <div key={r.notify_id} className="grid grid-cols-[1.4fr_1fr_1fr_46px] gap-4 px-4 py-3 border-b border-border last:border-b-0 text-sm items-center">
                  <span className="truncate">{r.email}</span>
                  <span className="text-white/70 truncate">{r.name || "—"}</span>
                  <span className="text-white/45 meta-mono truncate">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
                  <button onClick={() => removeOne(r.notify_id)} className="text-white/40 hover:text-primary" title="Remove request" data-testid={`notify-delete-${r.notify_id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============= EDITORIAL =============
function EditorialPanel({ items, reload }) {
  const [editing, setEditing] = useState(null);
  const blank = { title: "", slug: "", dek: "", body: "", author: "", cover_image_url: "", read_time: "", published: true };
  const save = async (form) => {
    try {
      if (form.article_id) await api.patch(`/admin/editorial/${form.article_id}`, form);
      else await api.post("/admin/editorial", form);
      toast.success("Saved"); setEditing(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/editorial/${id}`); reload();
  };
  return (
    <div>
      <button onClick={() => setEditing(blank)} data-testid="editorial-new" className="btn-pill btn-coral mb-4"><Plus size={13} /> New article</button>
      {editing && <EditorialForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
      <div className="border border-border divide-y divide-border mt-2" data-testid="admin-editorial">
        {items.length === 0 ? <p className="p-8 font-mono text-sm text-white/60">No articles. Write the first one.</p> : items.map((a) => (
          <div key={a.article_id} className="p-5 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="meta-mono !text-primary">{a.read_time || "—"} · {a.published ? "PUBLISHED" : "DRAFT"}</p>
              <p className="font-display text-xl font-bold mt-1 truncate">{a.title}</p>
              <p className="text-sm text-white/70 mt-1 line-clamp-2">{a.dek}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing(a)} className="btn-pill" data-testid={`edit-${a.article_id}`}>Edit</button>
              <button onClick={() => remove(a.article_id)} className="btn-pill !p-2"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EditorialForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="border border-border bg-background p-5 mb-5 grid gap-3" data-testid="editorial-form">
      <In label="Title" v={form.title} on={(v) => setForm({ ...form, title: v })} testid="ed-title" />
      <In label="Slug (auto from title if blank)" v={form.slug} on={(v) => setForm({ ...form, slug: v })} />
      <In label="Author" v={form.author} on={(v) => setForm({ ...form, author: v })} />
      <In label="Read time (e.g. 8 min)" v={form.read_time} on={(v) => setForm({ ...form, read_time: v })} />
      <In label="Cover image URL" v={form.cover_image_url} on={(v) => setForm({ ...form, cover_image_url: v })} />
      <Area label="Dek (short summary)" v={form.dek} on={(v) => setForm({ ...form, dek: v })} rows={2} />
      <Area label="Body (markdown supported)" v={form.body} on={(v) => setForm({ ...form, body: v })} rows={8} testid="ed-body" />
      <label className="meta-mono flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> PUBLISHED
      </label>
      <div className="flex gap-2 mt-2">
        <button type="submit" className="btn-pill btn-coral" data-testid="ed-save"><Save size={13} /> Save</button>
        <button type="button" onClick={onCancel} className="btn-pill">Cancel</button>
      </div>
    </form>
  );
}

// ============= PODCAST =============
function PodcastPanel({ items, reload }) {
  const [editing, setEditing] = useState(null);
  const blank = { youtube_id: "", title: "", guest: "", episode_number: null, duration: "", description: "", published: true };
  const save = async (form) => {
    try {
      const clean = { ...form, episode_number: form.episode_number ? parseInt(form.episode_number, 10) : null };
      if (form.episode_id) await api.patch(`/admin/podcast/${form.episode_id}`, clean);
      else await api.post("/admin/podcast", clean);
      toast.success("Saved"); setEditing(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this episode?")) return;
    await api.delete(`/admin/podcast/${id}`); reload();
  };
  return (
    <div>
      <button onClick={() => setEditing(blank)} data-testid="podcast-new" className="btn-pill btn-coral mb-4"><Plus size={13} /> New episode</button>
      <p className="meta-mono mb-4 !text-white/55">— PASTE THE YOUTUBE VIDEO ID FROM youtube.com/watch?v=<span className="text-primary">XXXXXXX</span></p>
      {editing && <PodcastForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
      <div className="border border-border divide-y divide-border" data-testid="admin-podcast">
        {items.length === 0 ? <p className="p-8 font-mono text-sm text-white/60">No episodes yet.</p> : items.map((e) => (
          <div key={e.episode_id} className="p-5 flex items-start gap-4">
            <a href={`https://www.youtube.com/watch?v=${e.youtube_id}`} target="_blank" rel="noreferrer" className="w-32 shrink-0">
              <img src={`https://img.youtube.com/vi/${e.youtube_id}/mqdefault.jpg`} alt="" className="w-full" />
            </a>
            <div className="flex-1 min-w-0">
              <p className="meta-mono !text-secondary">EP{e.episode_number ?? "—"} · {e.duration || "—"} · {e.published ? "LIVE" : "HIDDEN"}</p>
              <p className="font-display text-xl font-bold mt-1 truncate">{e.title}</p>
              <p className="text-sm text-white/65 mt-1">Guest: {e.guest || "—"}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing(e)} className="btn-pill" data-testid={`pod-edit-${e.episode_id}`}>Edit</button>
              <button onClick={() => remove(e.episode_id)} className="btn-pill !p-2"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PodcastForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="border border-border bg-background p-5 mb-5 grid gap-3" data-testid="podcast-form">
      <In label="YouTube ID (the bit after watch?v=)" v={form.youtube_id} on={(v) => setForm({ ...form, youtube_id: v })} testid="pod-yt" />
      <In label="Title" v={form.title} on={(v) => setForm({ ...form, title: v })} testid="pod-title" />
      <In label="Guest" v={form.guest} on={(v) => setForm({ ...form, guest: v })} />
      <In label="Episode number" type="number" v={form.episode_number || ""} on={(v) => setForm({ ...form, episode_number: v })} />
      <In label="Duration (e.g. 52 min)" v={form.duration} on={(v) => setForm({ ...form, duration: v })} />
      <Area label="Description" v={form.description} on={(v) => setForm({ ...form, description: v })} rows={3} />
      <label className="meta-mono flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> PUBLISHED
      </label>
      <div className="flex gap-2 mt-2">
        <button type="submit" className="btn-pill btn-coral" data-testid="pod-save"><Save size={13} /> Save</button>
        <button type="button" onClick={onCancel} className="btn-pill">Cancel</button>
      </div>
    </form>
  );
}

// ============= ADVISORS =============
function AdvisorsPanel({ items, reload }) {
  const [editing, setEditing] = useState(null);
  const blank = { slug: "", name: "", role: "", bio: "", discipline: "", image_url: "", rate_inr: 2500, email: "", availability: [{ days: [], start: "", end: "" }, { days: [], start: "", end: "" }], blackout_dates: [], active: true };
  const save = async (form) => {
    try {
      const { _exists, ...rest } = form;
      // Drop empty availability windows so backend doesn't store noise
      const availability = (rest.availability || []).filter((w) => w && (w.days || []).length > 0 && w.start && w.end);
      // Dedup + sort blackout dates
      const blackout_dates = Array.from(new Set((rest.blackout_dates || []).filter(Boolean))).sort();
      const clean = { ...rest, rate_inr: parseInt(form.rate_inr, 10) || 0, email: (form.email || "").trim(), availability, blackout_dates };
      if (_exists) await api.patch(`/admin/advisors/${form.slug}`, clean);
      else await api.post("/admin/advisors", clean);
      toast.success("Saved"); setEditing(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (slug) => {
    if (!window.confirm("Delete this advisor?")) return;
    await api.delete(`/admin/advisors/${slug}`); reload();
  };
  const copyShareLink = async (slug) => {
    const url = `${window.location.origin}/unfair-advantage?advisor=${encodeURIComponent(slug)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Copied · ${url}`);
    } catch (_e) {
      // Fallback for older browsers / non-HTTPS contexts
      window.prompt("Copy this share link:", url);
    }
  };
  return (
    <div>
      <button onClick={() => setEditing(blank)} data-testid="advisor-new" className="btn-pill btn-coral mb-4"><Plus size={13} /> New advisor</button>
      {editing && <AdvisorForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
      <div className="border border-border divide-y divide-border" data-testid="admin-advisors">
        {items.length === 0 ? <p className="p-8 font-mono text-sm text-white/60">Only seed advisors visible. Add custom ones above.</p> : items.map((a) => (
          <div key={a.slug} className="p-5 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="meta-mono !text-primary">{a.discipline?.toUpperCase() || "—"} · ₹{a.rate_inr?.toLocaleString("en-IN")} / session · {a.active ? "ACTIVE" : "PAUSED"}</p>
              <p className="font-display text-xl font-bold mt-1">{a.name}</p>
              <p className="text-sm text-white/70 mt-1">{a.role}</p>
              <p className="meta-mono !text-white/45 mt-2 truncate" data-testid={`adv-share-url-${a.slug}`}>
                — SHARE · /unfair-advantage?advisor={a.slug}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => copyShareLink(a.slug)} className="btn-pill" data-testid={`adv-share-${a.slug}`} title="Copy shareable booking link">
                <LinkIcon size={13} /> Copy link
              </button>
              <button onClick={() => setEditing({ ...a, _exists: true })} className="btn-pill" data-testid={`adv-edit-${a.slug}`}>Edit</button>
              <button onClick={() => remove(a.slug)} className="btn-pill !p-2"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdvisorForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large. Max 8MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // NOTE: Do NOT set Content-Type manually — the browser must set it to
      // `multipart/form-data; boundary=...` automatically. Overriding it strips the boundary.
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearPhoto = () => setForm({ ...form, image_url: "" });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="border border-border bg-background p-5 mb-5 grid gap-3" data-testid="advisor-form">
      <In label="Slug (URL-safe, lowercase, unique)" v={form.slug} on={(v) => setForm({ ...form, slug: v })} testid="adv-slug" disabled={form._exists} />
      <In label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="adv-name" />
      <In label="Role / one-liner" v={form.role} on={(v) => setForm({ ...form, role: v })} />
      <In label="Discipline" v={form.discipline} on={(v) => setForm({ ...form, discipline: v })} />

      {/* PHOTO uploader */}
      <div>
        <p className="meta-mono mb-1.5">— PHOTO</p>
        <div className="border border-border p-3 grid gap-3" data-testid="adv-photo-block">
          {form.image_url ? (
            <div className="flex items-start gap-4">
              <img src={form.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${form.image_url}` : form.image_url} alt="" className="w-28 h-28 object-cover border border-border" data-testid="adv-photo-preview" />
              <div className="flex-1 grid gap-2">
                <p className="meta-mono !text-white/55 break-all">{form.image_url}</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => fileRef.current?.click()} className="btn-pill" data-testid="adv-photo-replace">Replace photo</button>
                  <button type="button" onClick={clearPhoto} className="btn-pill" data-testid="adv-photo-remove"><Trash2 size={12} /> Remove</button>
                </div>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="border border-dashed border-border hover:border-primary p-8 text-center transition" data-testid="adv-photo-upload">
              <Plus size={20} className="mx-auto mb-2 text-white/50" />
              <p className="meta-mono">{uploading ? "UPLOADING…" : "CLICK TO UPLOAD A PHOTO"}</p>
              <p className="meta-mono mt-1 !text-white/45">PNG · JPG · WEBP · MAX 8MB</p>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onPick} className="hidden" data-testid="adv-photo-file" />
        </div>
      </div>

      <In label="Rate (INR per session)" type="number" v={form.rate_inr} on={(v) => setForm({ ...form, rate_inr: v })} />
      <In label="Email (for booking notifications)" type="email" v={form.email || ""} on={(v) => setForm({ ...form, email: v })} testid="adv-email" />
      <Area label="Bio" v={form.bio} on={(v) => setForm({ ...form, bio: v })} rows={4} />

      {/* AVAILABILITY WINDOWS */}
      <div>
        <p className="meta-mono mb-2 !text-primary">— AVAILABILITY (IST · 30-MIN SLOTS)</p>
        <p className="meta-mono !text-white/50 mb-3">Set up to two weekly windows. Booker sees 30-min slots inside these times only. Leave blank to show &quot;slots coming soon&quot;.</p>
        {[0, 1].map((i) => (
          <AvailabilityRow
            key={i}
            idx={i}
            value={(form.availability || [])[i] || { days: [], start: "", end: "" }}
            onChange={(w) => {
              const next = [...(form.availability || [{ days: [], start: "", end: "" }, { days: [], start: "", end: "" }])];
              while (next.length < 2) next.push({ days: [], start: "", end: "" });
              next[i] = w;
              setForm({ ...form, availability: next });
            }}
          />
        ))}
      </div>

      {/* BLACKOUT DATES */}
      <BlackoutDates
        value={form.blackout_dates || []}
        onChange={(dates) => setForm({ ...form, blackout_dates: dates })}
      />

      <label className="meta-mono flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> ACTIVE (visible to members)
      </label>
      <div className="flex gap-2 mt-2">
        <button type="submit" disabled={uploading} className="btn-pill btn-coral" data-testid="adv-save"><Save size={13} /> Save</button>
        <button type="button" onClick={onCancel} className="btn-pill">Cancel</button>
      </div>
    </form>
  );
}

const WEEK_DAYS = [
  { i: 0, k: "MON" }, { i: 1, k: "TUE" }, { i: 2, k: "WED" }, { i: 3, k: "THU" },
  { i: 4, k: "FRI" }, { i: 5, k: "SAT" }, { i: 6, k: "SUN" },
];

function AvailabilityRow({ idx, value, onChange }) {
  const days = Array.isArray(value.days) ? value.days : [];
  const toggleDay = (d) => {
    const set = new Set(days);
    if (set.has(d)) set.delete(d); else set.add(d);
    onChange({ ...value, days: Array.from(set).sort() });
  };
  return (
    <div className="border border-border p-3 mb-2" data-testid={`adv-availability-${idx}`}>
      <p className="meta-mono mb-2 !text-white/60">SLOT {idx + 1}</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {WEEK_DAYS.map((d) => {
          const on = days.includes(d.i);
          return (
            <button
              type="button"
              key={d.i}
              data-testid={`adv-avail-${idx}-day-${d.k.toLowerCase()}`}
              onClick={() => toggleDay(d.i)}
              className={`font-mono text-[10px] tracking-[0.18em] px-3 py-1.5 border ${on ? "bg-primary text-white border-primary" : "border-border text-white/70 hover:border-white"}`}
            >{d.k}</button>
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="meta-mono mb-1 !text-white/50">START (HH:MM · 24h)</p>
          <input
            type="time"
            data-testid={`adv-avail-${idx}-start`}
            value={value.start || ""}
            onChange={(e) => onChange({ ...value, start: e.target.value })}
            className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-1.5 text-sm font-mono"
          />
        </div>
        <div>
          <p className="meta-mono mb-1 !text-white/50">END</p>
          <input
            type="time"
            data-testid={`adv-avail-${idx}-end`}
            value={value.end || ""}
            onChange={(e) => onChange({ ...value, end: e.target.value })}
            className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-1.5 text-sm font-mono"
          />
        </div>
      </div>
    </div>
  );
}

function BlackoutDates({ value, onChange }) {
  const [pick, setPick] = React.useState("");
  const today = new Date().toISOString().slice(0, 10);
  const dates = Array.from(new Set(value || [])).sort();
  const add = () => {
    if (!pick) return;
    if (dates.includes(pick)) { setPick(""); return; }
    onChange([...dates, pick].sort());
    setPick("");
  };
  const remove = (d) => onChange(dates.filter((x) => x !== d));
  return (
    <div data-testid="adv-blackouts">
      <p className="meta-mono mb-2 !text-primary">— BLACKOUT DATES (UNAVAILABLE)</p>
      <p className="meta-mono !text-white/50 mb-3">Add specific dates when this advisor is NOT available (holidays, travel, off-days). These override the weekly availability windows above.</p>
      <div className="border border-border p-3">
        <div className="flex flex-wrap gap-2 items-end mb-3">
          <div>
            <p className="meta-mono mb-1 !text-white/50">PICK A DATE</p>
            <input
              type="date"
              data-testid="adv-blackout-picker"
              min={today}
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              className="bg-transparent outline-none border-b border-border focus:border-primary py-1.5 text-sm font-mono"
            />
          </div>
          <button type="button" onClick={add} disabled={!pick} className="btn-pill disabled:opacity-40" data-testid="adv-blackout-add">
            <Plus size={12} /> Block this date
          </button>
        </div>
        {dates.length === 0 ? (
          <p className="meta-mono !text-white/40">— NO BLACKOUT DATES SET</p>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid="adv-blackout-list">
            {dates.map((d) => (
              <span
                key={d}
                data-testid={`adv-blackout-chip-${d}`}
                className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] px-3 py-1.5 border border-border bg-black/20"
              >
                {new Date(d + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" }).toUpperCase()}
                <button type="button" onClick={() => remove(d)} data-testid={`adv-blackout-remove-${d}`} className="text-white/50 hover:text-primary">
                  <Trash2 size={11} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============= SITE SETTINGS =============
function SettingsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/site/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return <p className="meta-mono">Loading…</p>;
  const save = async () => {
    try { const { data } = await api.patch("/admin/site/settings", s); setS(data); toast.success("Settings saved"); }
    catch (e) { toast.error("Save failed"); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-px bg-border" data-testid="admin-settings">
      <div className="bg-background p-6">
        <p className="meta-mono mb-4 !text-primary">— YOUTUBE / PODCAST</p>
        <In label="YouTube channel URL" v={s.youtube_channel_url} on={(v) => setS({ ...s, youtube_channel_url: v })} testid="set-yt-url" />
        <div className="h-4" />
        <In label="Handle (e.g. @anartizen)" v={s.youtube_channel_handle} on={(v) => setS({ ...s, youtube_channel_handle: v })} />
        <div className="h-4" />
        <In label="Featured episode YouTube ID (shown on the landing page)" v={s.featured_episode_id} on={(v) => setS({ ...s, featured_episode_id: v })} testid="set-feat-id" />
        <div className="h-4" />
        <In label="Featured episode label (optional)" v={s.featured_episode_title} on={(v) => setS({ ...s, featured_episode_title: v })} />
        {s.featured_episode_id && (
          <div className="mt-4 border border-border">
            <iframe
              src={`https://www.youtube.com/embed/${s.featured_episode_id}`}
              title="Preview"
              className="w-full aspect-video"
              allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}
        <a href={s.youtube_channel_url} target="_blank" rel="noreferrer" className="btn-pill mt-4 inline-flex"><Youtube size={14} /> Open channel</a>
      </div>
      <div className="bg-background p-6">
        <p className="meta-mono mb-4 !text-primary">— NEWSLETTER</p>
        <label className="meta-mono flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={s.newsletter_enabled} onChange={(e) => setS({ ...s, newsletter_enabled: e.target.checked })} data-testid="set-news-on" /> NEWSLETTER VISIBLE ON LANDING
        </label>
        <div className="h-4" />
        <Area label="Newsletter blurb (shown on the landing page)" v={s.newsletter_blurb} on={(v) => setS({ ...s, newsletter_blurb: v })} rows={3} />
      </div>
      <div className="col-span-full bg-background p-6">
        <GoogleCalendarPanel />
      </div>
      <div className="col-span-full bg-background p-6">
        <button onClick={save} className="btn-pill btn-coral" data-testid="settings-save"><Save size={13} /> Save settings</button>
      </div>
    </div>
  );
}

// ============= GOOGLE CALENDAR CONNECTION =============
function GoogleCalendarPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/gcal/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ configured: false, connected: false }));
  }, []);
  useEffect(() => {
    load();
    // Surface the ?gcal= result banner from the OAuth callback redirect
    const params = new URLSearchParams(window.location.search);
    const gcal = params.get("gcal");
    if (gcal === "connected") toast.success(`Google Calendar connected · ${params.get("email") || ""}`);
    else if (gcal === "error") toast.error(`Google Calendar connect failed · ${params.get("reason") || "unknown"}`);
  }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      // Pass the admin JWT as a query param — backend accepts it since we're doing a browser redirect
      const adminToken = window.localStorage.getItem("admin_token") || "";
      const { data } = await api.get(`/admin/gcal/start${adminToken ? `?token=${encodeURIComponent(adminToken)}` : ""}`);
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error("Could not start Google connect");
        setBusy(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Google connect failed to start");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? Future booking invites will stop sending until you reconnect.")) return;
    try {
      await api.post("/admin/gcal/disconnect");
      toast.success("Google Calendar disconnected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    }
  };

  if (!status) return <p className="meta-mono">Loading Google Calendar status…</p>;

  return (
    <div data-testid="gcal-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="meta-mono !text-primary">— GOOGLE CALENDAR · BOOKING INVITES</p>
        <span className={`meta-mono ${status.connected ? "!text-primary" : "!text-secondary"}`}>
          {status.connected ? "● CONNECTED" : "○ NOT CONNECTED"}
        </span>
      </div>
      {!status.configured ? (
        <p className="text-sm text-white/70 leading-relaxed">
          Google OAuth credentials aren&apos;t set on the server. Add <span className="font-mono text-primary">GOOGLE_CLIENT_ID</span> and <span className="font-mono text-primary">GOOGLE_CLIENT_SECRET</span> in the Deployment Panel and redeploy.
        </p>
      ) : status.connected ? (
        <div className="space-y-2">
          <p className="text-sm text-white/80">
            Connected as <span className="font-mono text-primary" data-testid="gcal-connected-email">{status.connected_email}</span>
            {status.connected_at && <> · since {new Date(status.connected_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</>}
          </p>
          <p className="text-xs text-white/50">Every new Unfair Advantage booking creates an event on this calendar with a Meet link, and invites both the member and the advisor.</p>
          <button onClick={disconnect} className="btn-pill mt-2" data-testid="gcal-disconnect"><LogOut size={13} /> Disconnect</button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-white/80 leading-relaxed">
            Not connected yet. Click below and sign in as <span className="font-mono text-primary">{status.organizer_email}</span> to authorise Meet-link creation and calendar invites for every booking.
          </p>
          <p className="text-xs text-white/50">
            Important — sign in with the <strong>organiser Google account</strong> ({status.organizer_email}), not your personal Gmail. All future booking events will live on that calendar.
          </p>
          <button onClick={connect} disabled={busy} className="btn-pill btn-coral" data-testid="gcal-connect">
            {busy ? "Redirecting to Google…" : "Connect Google Calendar"} <ArrowRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

// ============= SHARED INPUTS =============
function In({ label, v, on, type = "text", testid, disabled = false }) {
  return (
    <div>
      <p className="meta-mono mb-1.5">— {label.toUpperCase()}</p>
      <input data-testid={testid} type={type} value={v ?? ""} disabled={disabled} onChange={(e) => on(e.target.value)} className="w-full bg-transparent border border-border p-2.5 outline-none focus:border-primary text-sm disabled:opacity-50" />
    </div>
  );
}
function Area({ label, v, on, rows = 3, testid }) {
  return (
    <div>
      <p className="meta-mono mb-1.5">— {label.toUpperCase()}</p>
      <textarea data-testid={testid} rows={rows} value={v ?? ""} onChange={(e) => on(e.target.value)} className="w-full bg-transparent border border-border p-2.5 outline-none focus:border-primary text-sm font-mono" />
    </div>
  );
}

// ============= ADMIN LOGIN SCREEN (email+password → email OTP) =============
function AdminLoginScreen({ onAuthenticated }) {
  const [step, setStep] = useState("creds"); // creds | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submitCreds = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { email: email.trim().toLowerCase(), password });
      setChallenge(data);
      setStep("otp");
      if (data.dev_otp) {
        toast.message(`Email mocked — OTP: ${data.dev_otp}`, { duration: 8000 });
      } else {
        toast.success(`Code sent to ${data.email}. Check your inbox.`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not sign in";
      setErr(typeof msg === "string" ? msg : "Could not sign in");
    } finally { setBusy(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/verify-otp", { challenge_id: challenge.challenge_id, otp: otp.trim() });
      setAdminToken(data.token);
      toast.success("Welcome back.");
      onAuthenticated();
    } catch (e) {
      const msg = e.response?.data?.detail || "Wrong code";
      setErr(typeof msg === "string" ? msg : "Wrong code");
    } finally { setBusy(false); }
  };

  return (
    <PageShell>
      <section className="relative grid-bg min-h-[80vh] flex items-center justify-center">
        <SlideCorner right="ADMIN · SIGN IN" />
        <div className="w-full max-w-md px-5">
          <SectionLabel><ShieldCheck size={11} /> Restricted</SectionLabel>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-[0.95] tracking-tight">
            {step === "creds" ? <>Sign in to the <span className="hl-coral">control room.</span></> : <>Check your <span className="hl-cyan">inbox.</span></>}
          </h1>

          {step === "creds" ? (
            <form onSubmit={submitCreds} className="mt-10 border border-border bg-background p-5 grid gap-4" data-testid="admin-login-form">
              <div>
                <p className="meta-mono mb-2">— EMAIL</p>
                <input
                  data-testid="admin-login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-2 text-base"
                  placeholder="you@anartizen.com"
                />
              </div>
              <div>
                <p className="meta-mono mb-2">— PASSWORD</p>
                <input
                  data-testid="admin-login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-2 text-base"
                  placeholder="••••••••"
                />
              </div>
              {err && <p className="meta-mono !text-primary" data-testid="admin-login-error">— {err.toUpperCase()}</p>}
              <button disabled={busy} data-testid="admin-login-submit" className="btn-pill btn-coral w-full justify-center mt-2">
                {busy ? "Checking…" : <>Continue <ArrowRight size={14} /></>}
              </button>
              <p className="meta-mono !text-white/45 mt-2">— STEP 1 OF 2 · WE&apos;LL EMAIL A 6-DIGIT CODE NEXT</p>
            </form>
          ) : (
            <form onSubmit={submitOtp} className="mt-10 border border-border bg-background p-5 grid gap-4" data-testid="admin-otp-form">
              <p className="meta-mono">— CODE SENT TO <span className="text-primary">{challenge?.email}</span></p>
              <div>
                <p className="meta-mono mb-2">— 6-DIGIT CODE</p>
                <input
                  data-testid="admin-otp-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
                  autoFocus
                  className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-2 text-3xl font-mono tracking-[0.5em] text-center"
                  placeholder="••••••"
                />
              </div>
              {challenge?.dev_otp && (
                <p className="meta-mono !text-cream" data-testid="admin-otp-dev-hint">
                  — DEV MODE · CODE: <span className="text-primary font-bold tracking-[0.3em]">{challenge.dev_otp}</span>
                </p>
              )}
              {err && <p className="meta-mono !text-primary" data-testid="admin-otp-error">— {err.toUpperCase()}</p>}
              <button disabled={busy || otp.length < 6} data-testid="admin-otp-submit" className="btn-pill btn-coral w-full justify-center mt-2">
                {busy ? "Verifying…" : <>Sign in <ArrowRight size={14} /></>}
              </button>
              <button type="button" onClick={() => { setStep("creds"); setOtp(""); setErr(""); }} className="meta-mono !text-white/55 mt-2">— BACK</button>
            </form>
          )}
        </div>
      </section>
    </PageShell>
  );
}

// ============= ROW (existing pipeline/booking inline expand) =============
function Row({ row, tab, onUpdate, onReload }) {
  const [open, setOpen] = useState(false);
  const id = row.brief_id || row.application_id || row.submission_id || row.booking_id || row.subscription_id;
  const title = row.company || row.title || row.name || row.advisor_name || row.tier;
  const subtitle = row.project_type || row.program || row.pipeline || row.slot_iso || row.amount_inr;
  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete this ${tab.slice(0, -1)}? This can't be undone.`)) return;
    try { await api.delete(`/admin/${tab}/${id}`); toast.success("Deleted"); onReload?.(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div data-testid={`admin-row-${id}`} className="p-5">
      <button onClick={() => setOpen(!open)} className="w-full flex items-start justify-between gap-6 text-left">
        <div className="flex-1">
          <p className="meta-mono !text-primary mb-1">{subtitle}</p>
          <p className="font-display text-xl md:text-2xl font-bold leading-tight">{title}</p>
          {row.contact_email || row.email ? <p className="meta-mono mt-2 flex items-center gap-2"><Mail size={10} /> {row.contact_email || row.email}</p> : null}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {row.status && <span className={`meta-mono ${row.status === "accepted" ? "!text-secondary" : row.status === "rejected" ? "!text-white/40" : "!text-primary"}`}>{row.status?.toUpperCase()}</span>}
          <ChevronDown size={14} className={`transition ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="mt-5 border-t border-border pt-5 grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-3 text-sm">
            {Object.entries(row).filter(([k]) => !["matched_artists","_id"].includes(k)).map(([k, v]) => (
              <div key={k} className="flex gap-4">
                <span className="meta-mono w-40 shrink-0">— {k}</span>
                <span className="text-white/85 break-all">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-3">
            {tab !== "subscriptions" && (
              <>
                <p className="meta-mono">— UPDATE STATUS</p>
                {STATUSES.map((s) => (
                  <button key={s} data-testid={`admin-set-${id}-${s}`} onClick={() => onUpdate(tab, id, s)} className={`btn-pill w-full justify-center ${row.status === s ? "btn-coral" : ""}`}>{s}</button>
                ))}
              </>
            )}
            <p className="meta-mono pt-3 border-t border-border mt-3">— DANGER ZONE</p>
            <button onClick={remove} data-testid={`admin-delete-${id}`} className="btn-pill w-full justify-center text-primary hover:bg-primary/10"><Trash2 size={12} /> Delete record</button>
          </div>
        </div>
      )}
    </div>
  );
}
